import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'

export type BugReportStatus = 'open' | 'fixing' | 'fixed' | 'resolved' | 'error'

export interface BugReport {
  id: string
  app_id: string
  status: BugReportStatus
  description: string
  created_at: string
  container_id?: string | null
}

interface BugReportsState {
  items: BugReport[]
  resolvedItems: BugReport[]
  resolvedCount: number
  resolvedCursor: string | null
  resolvedHasMore: boolean
  resolvedLoading: boolean
  loading: boolean
  error: string | null
  submitting: boolean
  lastSubmittedAt: number | null
}

const initialState: BugReportsState = {
  items: [],
  resolvedItems: [],
  resolvedCount: 0,
  resolvedCursor: null,
  resolvedHasMore: false,
  resolvedLoading: false,
  loading: false,
  error: null,
  submitting: false,
  lastSubmittedAt: null,
}

export const fetchBugReports = createAsyncThunk(
  'bugReports/fetchBugReports',
  async (appId: string) => {
    const response = await fetch(`/api/bug-reports/${appId}`)
    if (!response.ok) throw new Error('Failed to fetch bug reports')
    return await response.json() as { items: BugReport[]; hasMore: boolean; nextCursor: string | null; resolvedCount: number }
  },
)

export const fetchResolvedBugReports = createAsyncThunk(
  'bugReports/fetchResolvedBugReports',
  async ({ appId, cursor }: { appId: string; cursor?: string | null }) => {
    const params = new URLSearchParams({ status: 'resolved', limit: '20' })
    if (cursor) params.set('cursor', cursor)
    const response = await fetch(`/api/bug-reports/${appId}?${params}`)
    if (!response.ok) throw new Error('Failed to fetch resolved bug reports')
    return await response.json() as { items: BugReport[]; hasMore: boolean; nextCursor: string | null; resolvedCount: number }
  },
)

export const submitBugReport = createAsyncThunk(
  'bugReports/submitBugReport',
  async ({ appId, description }: { appId: string; description: string }) => {
    const response = await fetch(`/api/bug-report-webhook/${appId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ body: description, referrer: 'app-page' }),
    })
    if (!response.ok) {
      const data = await response.json().catch(() => ({}))
      throw new Error(data.error || `Failed to submit bug report: HTTP ${response.status}`)
    }
    return response.json() as Promise<BugReport>
  },
)

export const deleteBugReport = createAsyncThunk(
  'bugReports/deleteBugReport',
  async ({ appId, id }: { appId: string; id: string }) => {
    const response = await fetch(`/api/bug-reports/${appId}/${id}`, {
      method: 'DELETE',
    })
    if (!response.ok) {
      const data = await response.json().catch(() => ({}))
      throw new Error(data.error || 'Failed to delete bug report')
    }
    return id
  },
)

const bugReportsSlice = createSlice({
  name: 'bugReports',
  initialState,
  reducers: {
    clearBugReports() {
      return initialState
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBugReports.pending, (state) => {
        if (state.items.length === 0) state.loading = true
        state.error = null
      })
      .addCase(fetchBugReports.fulfilled, (state, action) => {
        state.loading = false
        const allItems = action.payload.items
        state.items = allItems.filter((b) => b.status === 'open' || b.status === 'fixing')
        const fetchedResolved = allItems.filter((b) => b.status === 'fixed' || b.status === 'resolved' || b.status === 'error')
        state.resolvedItems = fetchedResolved
        state.resolvedCount = action.payload.resolvedCount
        state.resolvedHasMore = fetchedResolved.length < action.payload.resolvedCount
        state.resolvedCursor = fetchedResolved.length > 0 ? fetchedResolved[fetchedResolved.length - 1].created_at : null
      })
      .addCase(fetchBugReports.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to fetch bug reports'
      })
      .addCase(fetchResolvedBugReports.pending, (state) => {
        state.resolvedLoading = true
      })
      .addCase(fetchResolvedBugReports.fulfilled, (state, action) => {
        state.resolvedLoading = false
        const existingIds = new Set(state.resolvedItems.map((b) => b.id))
        const newItems = action.payload.items.filter((b) => !existingIds.has(b.id))
        state.resolvedItems = [...state.resolvedItems, ...newItems]
        state.resolvedHasMore = action.payload.hasMore
        state.resolvedCursor = action.payload.nextCursor
        state.resolvedCount = action.payload.resolvedCount
      })
      .addCase(fetchResolvedBugReports.rejected, (state) => {
        state.resolvedLoading = false
      })
      .addCase(submitBugReport.pending, (state) => {
        state.submitting = true
      })
      .addCase(submitBugReport.fulfilled, (state, action) => {
        state.submitting = false
        state.items.push(action.payload)
        state.lastSubmittedAt = Date.now()
      })
      .addCase(submitBugReport.rejected, (state) => {
        state.submitting = false
      })
      .addCase(deleteBugReport.fulfilled, (state, action) => {
        const id = action.payload
        state.items = state.items.filter((b) => String(b.id) !== id)
        const wasResolved = state.resolvedItems.some((b) => String(b.id) === id)
        state.resolvedItems = state.resolvedItems.filter((b) => String(b.id) !== id)
        if (wasResolved && state.resolvedCount > 0) state.resolvedCount -= 1
      })
  },
})

export const { clearBugReports } = bugReportsSlice.actions
export default bugReportsSlice.reducer
